import _ from 'lodash';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import profileFieldsServiceApi from '../api/profile-fields.service.api';
import userServiceApi from '../api/user.service.api';
import { queryKeys } from '../api/query-client-registry';
import { AFFECTED_USERS_SCOPES } from '../constants/profile-fields.constants';

/**
 * React-query hooks for the per-org profile-fields domain. All consumers should
 * reach the profile-fields API through these hooks.
 *
 * Option mutations invalidate the list rather than patching the cache: the
 * server re-sorts options alphabetically by label, and a delete-with-replace can
 * cascade `parentOptionId` across sibling fields — so a refetch is the reliable
 * source of truth.
 *
 * @typedef {import('../api/profile-fields.service.api').ProfileFieldsByGroup} ProfileFieldsByGroup
 */

/** Hierarchical query keys. Not org-scoped — the cache is cleared on org switch. */
export const profileFieldsKeys = {
  all: ['profileFields'],
  list: () => [...profileFieldsKeys.all, 'list'],
  affected: () => [...profileFieldsKeys.all, 'affected'],
  affectedCount: (fieldId, optionId, scope) => [
    ...profileFieldsKeys.affected(),
    'count',
    fieldId,
    optionId,
    scope,
  ],
  affectedUsers: (fieldId, optionId, scope) => [
    ...profileFieldsKeys.affected(),
    'users',
    fieldId,
    optionId,
    scope,
  ],
};

/** The backend's max page size. */
const AFFECTED_USERS_PAGE_SIZE = 500;

/** Per-org, per-field usage counts: `stats[fieldKey][optionCode] = number`. */
export const userStatsKeys = {
  all: queryKeys.userGroupStats,
};

// ---------- Reads ----------

/**
 * @param {object} [options] react-query options passthrough
 * @returns {import('@tanstack/react-query').UseQueryResult<ProfileFieldsByGroup>}
 */
export function useProfileFields(options = {}) {
  return useQuery({
    queryKey: profileFieldsKeys.list(),
    queryFn: ({ signal }) => profileFieldsServiceApi.getProfileFields({ signal }),
    ...options,
  });
}

/**
 * Aggregated user counts per profile-field value, used to show how many users
 * are affected before deleting an option. Reads the existing
 * `GET /user/groups/stats` endpoint (legacy `{ data, error }` envelope).
 * @param {object} [options] react-query options passthrough
 */
export function useUserGroupStats(options = {}) {
  return useQuery({
    queryKey: userStatsKeys.all,
    queryFn: async () => {
      const { data, error } = await userServiceApi.getStats();
      if (error) throw error;
      return data?.stats ?? {};
    },
    ...options,
  });
}

/**
 * How many users deleting the option affects — by default those who would lose
 * their dependent values (role / level of training); `scope: 'HOLDERS'` for
 * everyone holding it. Fetches a one-row page and reads only the exact `total`,
 * so it is cheap to poll. Refetched on mount and on window focus.
 * @param {{ fieldId: string, optionId: string, scope?: 'DEPENDENTS'|'HOLDERS' }} args
 * @param {object} [options] react-query options passthrough
 */
export function useProfileFieldOptionAffectedCount(
  { fieldId, optionId, scope = AFFECTED_USERS_SCOPES.DEPENDENTS },
  options = {},
) {
  return useQuery({
    queryKey: profileFieldsKeys.affectedCount(fieldId, optionId, scope),
    queryFn: async ({ signal }) => {
      const { total } = await profileFieldsServiceApi.getOptionAffectedUsers(
        { fieldId, optionId, scope, page: 1, pageSize: 1 },
        { signal },
      );
      return total;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    // A failure (e.g. a backend without the route) should surface at once, not
    // after retries — and not re-retry on every poll.
    retry: false,
    ...options,
  });
}

/**
 * Every user deleting the option affects (see {@link useProfileFieldOptionAffectedCount}
 * for `scope`), collected across pages. The pages are fetched in parallel, so the
 * list is best-effort if subscriptions change mid-fetch; duplicates are dropped by
 * `userId`. Only `total` is exact.
 * @param {{ fieldId: string, optionId: string, scope?: 'DEPENDENTS'|'HOLDERS' }} args
 * @param {object} [options] react-query options passthrough
 */
export function useProfileFieldOptionAffectedUsers(
  { fieldId, optionId, scope = AFFECTED_USERS_SCOPES.DEPENDENTS },
  options = {},
) {
  return useQuery({
    queryKey: profileFieldsKeys.affectedUsers(fieldId, optionId, scope),
    queryFn: async ({ signal }) => {
      const getPage = page =>
        profileFieldsServiceApi.getOptionAffectedUsers(
          { fieldId, optionId, scope, page, pageSize: AFFECTED_USERS_PAGE_SIZE },
          { signal },
        );
      const first = await getPage(1);
      const pageCount = Math.ceil(first.total / AFFECTED_USERS_PAGE_SIZE);
      const rest = await Promise.all(
        Array.from({ length: Math.max(pageCount - 1, 0) }, (__, i) => getPage(i + 2)),
      );
      const users = _.uniqBy(
        [first, ...rest].flatMap(page => page.users),
        user => user.userId ?? user,
      );
      return { total: first.total, users };
    },
    staleTime: 0,
    retry: false,
    ...options,
  });
}

// ---------- Mutations ----------

export function useAddProfileFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, label, parentOptionId }) =>
      profileFieldsServiceApi.addOption({ fieldId, label, parentOptionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileFieldsKeys.list() });
    },
  });
}

export function useUpdateProfileFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, optionId, label }) =>
      profileFieldsServiceApi.updateOption({ fieldId, optionId, label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profileFieldsKeys.list() });
    },
  });
}

export function useDeleteProfileFieldOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, optionId, shouldReplace, replacementOptionId }) =>
      profileFieldsServiceApi.deleteOption({
        fieldId,
        optionId,
        shouldReplace,
        replacementOptionId,
      }),
    onSuccess: () => {
      // Reassign changed subscriptions → the fields list and the React Query
      // usage stats can shift. The affected USERS list + stats live in Redux
      // (usersManagement, cache-first), which this RQ cache can't invalidate —
      // the delete modal dispatches fetchUsers()/fetchUserStats() for those.
      qc.invalidateQueries({ queryKey: profileFieldsKeys.list() });
      qc.invalidateQueries({ queryKey: userStatsKeys.all });
      qc.removeQueries({ queryKey: profileFieldsKeys.affected() });
    },
  });
}
